import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation} from '@angular/core';
import {Document} from 'app/archivage/common/services/document/document.model';
import {Dua} from 'app/archivage/common/services/dua/dua.model';
import {DuaService} from 'app/archivage/common/services/dua/dua.service';
import {SubSink} from '@Common/core/utils/subsink';
import {FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DepositService} from 'app/archivage/common/services/deposit/deposit.service';
import {CategoryService} from 'app/archivage/common/services/category/category.service';
import {CategorySelectComponent, CategorySelectData} from 'app/archivage/common/components/category-select/category-select.component';
import {MatDialog} from '@angular/material/dialog';
import {CategoryModel, CategoryTreeNodeDump} from '../../../common/services/category/category.model';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {ArchiveValidator} from './numeric-validator';
import {Deposit} from '../../../common/services/deposit/deposit.model';

@Component({
  selector: 'app-document-edit',
  templateUrl: './document-edit.component.html',
  styleUrls: ['./document-edit.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DocumentEditComponent implements OnInit, OnDestroy, OnChanges {
  @Input() index: number;
  @Input() deposit: Deposit;
  @Input() document: Document;
  @Input() documentsFormArray: UntypedFormArray;
  @Input() forceEdit = false;

  @Output() delDocumentEvent = new EventEmitter();

  subSink = new SubSink();
  documentForm: UntypedFormGroup;
  duas: Dua[] = [];
  duasFiltered: Dua[] = this.duas;
  readonly categoryNamePlaceholder = 'Cliquer pour choisir la catégorie pour sélectionner une nature de document';
  categoryName: string = this.categoryNamePlaceholder;
  categories: CategoryModel[] = [];
  filteredOptions$: Observable<Dua[]>;
  duaFilter = new FormControl('');
  filteredDuas$: Observable<Dua[]>;
  ctrlFilterDua = new FormControl('');
  categoryTree: CategoryTreeNodeDump[] = [];
  protected readonly parseInt = parseInt;

  /**
   * TG-197 : obligé de mettre "type=text" pour Firefox (le number n'est pas détecté), tous les validators sont pris en compte
   * - est-ce un numérique : par ex. I998 ne passe pas
   * - required : pour traiter la chaine vide '' qui est envoyée sinon et provoque une 500
   * - le pattern de 1900 à 2099
   */
  IsCreationYearValid() {
    const _errors = this.documentForm?.controls['creationYear']?.errors;
    return !(_errors && (_errors.numeric || _errors.required || _errors.pattern));
  }
  IsDuaValid() {
    const _errors = this.documentForm?.controls['duaSelected']?.errors;
    return !(_errors && _errors.required);
  }
  constructor(
    public depositSvc: DepositService,
    private duaSvc: DuaService,
    private categorySvc: CategoryService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog
  ) {
    this.documentForm = this.formBuilder.group({
      duaSelected: ['', Validators.required],
      creationYear: ['',
        Validators.compose([ArchiveValidator.numericInput, Validators.pattern('^19\\d{2}|20\\d{2}$'), Validators.required])],
      description: [''],
    });
  }

  ngOnInit(): void {
    this.documentsFormArray.push(this.documentForm);
    this._getDuas();
    this._getCategories();
  }
  private _getDuas() {
    this.subSink.sink = this.duaSvc.listAllItemsStored()
      .subscribe(_data => {
        this.duas = _data.list?.filter((dua: Dua) => dua.entites.includes(this.deposit.dir_cmp));
        this.filteredDuas$ = this.ctrlFilterDua.valueChanges.pipe(
          startWith(''),
          map(value => this._filterDua(value || '')),
        );
      });
  }
  private _filterDua(value: string | Dua) {
    if (typeof value === "string") {
      const filterValue = value.toLowerCase();
      return this.duas?.filter(dua => dua.nature.toLowerCase().includes(filterValue)) || [];
    }
  }
  private _getCategories() {
    this.subSink.sink = this.categorySvc.storageCat$.subscribe(_data => this.categoryTree = _data);
    this.subSink.sink = this.categorySvc
      .listDumpStored()
      .subscribe(_data => this.categoryTree = _data);
  }
  getNatureValue() {
    return this.duaFilter?.getRawValue();
  }

  getCategorieValue() {
    const _cat = this.categoryTree.find(_categorie => _categorie.id === this.document?.dua_obj?.category);
    if (_cat) {
      return _cat.data.name;
    }
    return '';
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  delDocument() {
    this.delDocumentEvent.emit();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.document?.currentValue) {
      this._mapToDocumentForm(changes?.document?.currentValue);
    }
  }

  private _mapToDocumentForm(doc: Document) {
    if (this.documentForm) {
      this.documentForm.patchValue({
        duaSelected: doc.dua,
        creationYear: doc.creation_year,
        description: doc.description
      });
    }
  }

  onDuaSelected(value: number) {
    this.document.dua_obj = this.duasFiltered.filter(obj => obj.id === value)[0];
    this.documentForm.patchValue({duaSelected: this.document?.dua_obj?.id});
  }
  onSelectNature(dua: Dua) {
    this.ctrlFilterDua.setValue(dua.nature);
    this.document.dua_obj = dua;
    this.documentForm.patchValue({duaSelected: this.document?.dua_obj?.id});
  }
  showDua(dua: Dua) {
    return `
    Catégorie: ${dua.category_obj.name}
    ---
    Instruction: ${dua.instruction_obj.label}
    ---
    Nature: ${dua.nature}
    ---
    Observation: ${dua.observation}
    `;
  }

  selectCat() {
    const data: CategorySelectData = {
      displayDuasCount: true,
      excludeEmptyDua: true,
      entity: this.deposit.dir_cmp
    };
    const dialogRef = this.dialog.open(CategorySelectComponent, {data: data});
    dialogRef.updateSize('600px');
    dialogRef.updatePosition({top: '50px'});
    dialogRef.afterClosed().subscribe((category: CategoryModel) => {
        if (category) {
          this._reset_dua(category.id, category.name);
        }
      }
    );
  }
  private _reset_dua(categoryId: number, categoryName: string) {
    this.categoryName = categoryName;
    if (categoryId === 0) {
      this.duasFiltered = this.duas.filter(obj => obj.category !== categoryId);
    } else {
      this.duasFiltered = this.duas?.filter(obj => obj.category === categoryId);
    }
    this.document.dua_obj = null;
    this.documentForm.patchValue({
      duaSelected: null
    });
    this.filteredOptions$ = this.duaFilter.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '')),
    );
  }
  private _filter(value: string): Dua[] {
    const filterValue = value.toLowerCase();
    return this.duasFiltered.filter(option => option.nature.toLowerCase().includes(filterValue));
  }
}
